import { useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FORMAT_TEXT_COMMAND, IS_APPLE, $getSelection, $isRangeSelection, type TextFormatType } from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';
import { ListNode, $isListNode, INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, LinkSimple } from '../../../../design-system/icons';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { useDismissOnOutsidePointer } from '../../../../lib/useDismissOnOutsidePointer';

type ListType = 'bullet' | 'number';

interface ActiveState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  list: ListType | null;
  link: boolean;
}

const EMPTY: ActiveState = { bold: false, italic: false, underline: false, list: null, link: false };

const modifierKeyLabel = IS_APPLE ? '⌘' : 'Ctrl+';
const shortcutSuffix = (key: string) => ` (${modifierKeyLabel}${key})`;

const LIST_INSERT_COMMANDS = { bullet: INSERT_UNORDERED_LIST_COMMAND, number: INSERT_ORDERED_LIST_COMMAND } as const;

const isListType = (type: string | null): type is ListType => type === 'bullet' || type === 'number';

/** Figma's composer footer icons (505:50225): Bold, Italic, Underline, Link, bulleted list — nothing else
 *  (no headings/color/code, unlike scriptDoc's fuller Toolbar). */
/** `disabled` while the message box is hidden behind a writing skeleton: formatting would act on nothing visible.
 *  `allowLink` false drops the Link button (a first touch never carries a link). */
export function ComposerToolbar({ disabled = false, allowLink = true, numberedList = false }: { disabled?: boolean; allowLink?: boolean; numberedList?: boolean }) {
  const [editor] = useLexicalComposerContext();
  const [active, setActive] = useState<ActiveState>(EMPTY);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const sel = $getSelection();
        if (!$isRangeSelection(sel)) {
          setActive(EMPTY);
          return;
        }
        const anchorNode = sel.anchor.getNode();
        const listNode = $getNearestNodeOfType(anchorNode, ListNode);
        const listType = listNode && $isListNode(listNode) ? listNode.getListType() : null;
        const linkNode = $getNearestNodeOfType(anchorNode, LinkNode);
        setActive({
          bold: sel.hasFormat('bold'),
          italic: sel.hasFormat('italic'),
          underline: sel.hasFormat('underline'),
          list: isListType(listType) ? listType : null,
          link: $isLinkNode(linkNode),
        });
      });
    });
  }, [editor]);

  const linkButtonRef = useRef<HTMLButtonElement>(null);
  const closeLinkPopover = () => setLinkOpen(false);
  useDismissOnOutsidePointer(linkOpen, linkPopoverRef, closeLinkPopover);

  const format = (f: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, f);

  const toggleList = (type: ListType) => {
    if (active.list === type) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(LIST_INSERT_COMMANDS[type], undefined);
  };

  const openLinkPopover = () => {
    if (active.link) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    setLinkUrl('');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    setLinkOpen(false);
    linkButtonRef.current?.focus();
  };

  const noBlur = (e: React.MouseEvent) => e.preventDefault();
  const cls = (on: boolean) => (on ? 'bg-accent text-accent-foreground' : '');

  // Figma: 24px square icon buttons, 8px radius, 20px #171717 glyphs.
  const btn = (label: string, on: boolean, onClick: () => void, icon: React.ReactNode, tooltip: string = label, extra: { ref?: React.Ref<HTMLButtonElement>; expanded?: boolean } = {}) => (
    <Button
      ref={extra.ref}
      variant="ghost"
      size="icon-xs"
      type="button"
      title={tooltip}
      aria-label={label}
      aria-pressed={on}
      aria-expanded={extra.expanded}
      disabled={disabled}
      className={`rounded-lg text-(--icon-default) ${cls(on)}`}
      onMouseDown={noBlur}
      onClick={onClick}
    >
      {icon}
    </Button>
  );

  return (
    <div role="group" aria-label="Formatting" className="flex items-center gap-2">
      {btn('Bold', active.bold, () => format('bold'), <TextB size={20} />, `Bold${shortcutSuffix('B')}`)}
      {btn('Italic', active.italic, () => format('italic'), <TextItalic size={20} />, `Italic${shortcutSuffix('I')}`)}
      {btn('Underline', active.underline, () => format('underline'), <TextUnderline size={20} />, `Underline${shortcutSuffix('U')}`)}
      {allowLink ? (
        <div
          className="relative"
          ref={linkPopoverRef}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && linkOpen) {
              e.stopPropagation();
              closeLinkPopover();
              linkButtonRef.current?.focus();
            }
          }}
        >
          {btn('Link', active.link, openLinkPopover, <LinkSimple size={20} />, active.link ? 'Remove link' : 'Link', { ref: linkButtonRef, expanded: active.link ? undefined : linkOpen })}
          {linkOpen ? (
            <div role="group" aria-label="Insert link" className="absolute bottom-full left-0 z-10 mb-1 flex items-center gap-1.5 rounded-lg border border-border bg-popover p-1.5 shadow-md">
              <Input
                autoFocus
                aria-label="Link URL"
                placeholder="https://…"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                }}
                className="h-7 w-48 text-xs"
              />
              <Button type="button" size="sm" className="h-7" onMouseDown={noBlur} onClick={applyLink}>
                Apply
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {btn('Bulleted list', active.list === 'bullet', () => toggleList('bullet'), <ListBullets size={20} />)}
      {numberedList ? btn('Numbered list', active.list === 'number', () => toggleList('number'), <ListNumbers size={20} />) : null}
    </div>
  );
}
